export interface ProfilePlotPadding {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface ProfilePlotBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
}

export interface ProfileScale extends ProfilePlotBounds {
  minimumElevationM: number;
  maximumElevationM: number;
  elevationRangeM: number;
  totalDistanceM: number;
}

export interface ProfileHoverTarget {
  index: number;
  x: number;
}

export interface ProfileTooltipPlacement {
  onLeft: boolean;
  top: number;
}

export const PROFILE_PLOT_PADDING: Readonly<ProfilePlotPadding> = Object.freeze({ left: 30, right: 48, top: 25, bottom: 18 });
export const PROFILE_TOOLTIP_WIDTH_PX = 196;
export const PROFILE_TOOLTIP_HALF_HEIGHT_PX = 54;

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

export function profilePlotBounds(width: number, height: number, padding: Readonly<ProfilePlotPadding> = PROFILE_PLOT_PADDING): ProfilePlotBounds {
  const safeWidth = Math.max(1, width);
  const safeHeight = Math.max(1, height);
  const left = clamp(padding.left, 0, safeWidth - 1);
  const right = clamp(safeWidth - padding.right, left + 1, safeWidth);
  const top = clamp(padding.top, 0, safeHeight - 1);
  const bottom = clamp(safeHeight - padding.bottom, top + 1, safeHeight);
  return { left, right, top, bottom, width: Math.max(1, right - left), height: Math.max(1, bottom - top) };
}

export function createProfileScale(width: number, height: number, totalDistanceM: number, elevationValuesM: readonly number[]): ProfileScale {
  const bounds = profilePlotBounds(width, height);
  const finiteValues = elevationValuesM.filter(Number.isFinite);
  const rawMinimum = finiteValues.length ? Math.min(...finiteValues) : 0;
  const rawMaximum = finiteValues.length ? Math.max(...finiteValues) : 10;
  const rawRange = Math.max(1, rawMaximum - rawMinimum);
  const elevationPadding = Math.max(4, rawRange * 0.08);
  let minimumElevationM = Math.floor((rawMinimum - elevationPadding) / 10) * 10;
  let maximumElevationM = Math.ceil((rawMaximum + elevationPadding) / 10) * 10;
  if (maximumElevationM - minimumElevationM < 20) {
    const midpoint = (minimumElevationM + maximumElevationM) / 2;
    minimumElevationM = Math.floor((midpoint - 10) / 10) * 10;
    maximumElevationM = Math.ceil((midpoint + 10) / 10) * 10;
  }
  return {
    ...bounds,
    minimumElevationM,
    maximumElevationM,
    elevationRangeM: Math.max(1, maximumElevationM - minimumElevationM),
    totalDistanceM: Math.max(1, totalDistanceM),
  };
}

export function profileXAtDistance(distanceM: number, scale: Pick<ProfileScale, 'left' | 'width' | 'totalDistanceM'>): number {
  return scale.left + clamp(distanceM / scale.totalDistanceM, 0, 1) * scale.width;
}

export function profileDistanceAtX(x: number, scale: Pick<ProfileScale, 'left' | 'width' | 'totalDistanceM'>): number {
  return clamp((x - scale.left) / Math.max(1, scale.width), 0, 1) * scale.totalDistanceM;
}

export function profileYAtElevation(elevationM: number, scale: Pick<ProfileScale, 'top' | 'height' | 'minimumElevationM' | 'elevationRangeM'>): number {
  const normalized = (elevationM - scale.minimumElevationM) / scale.elevationRangeM;
  return scale.top + (1 - clamp(normalized, 0, 1)) * scale.height;
}

export function profileTickValues(scale: Pick<ProfileScale, 'minimumElevationM' | 'maximumElevationM' | 'elevationRangeM'>, count = 4): number[] {
  const safeCount = Math.max(2, Math.floor(count));
  return Array.from({ length: safeCount }, (_, index) => scale.maximumElevationM - index / (safeCount - 1) * scale.elevationRangeM);
}

export function nearestProfileSampleIndex(samples: readonly { distanceM: number }[], distanceM: number): number {
  if (samples.length < 2) return 0;
  let low = 0;
  let high = samples.length - 1;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if ((samples[middle]?.distanceM ?? 0) < distanceM) low = middle + 1;
    else high = middle;
  }
  if (low === 0) return 0;
  const before = samples[low - 1]?.distanceM ?? 0;
  const after = samples[low]?.distanceM ?? before;
  return Math.abs(distanceM - before) <= Math.abs(after - distanceM) ? low - 1 : low;
}

export function profileHoverTarget(samples: readonly { distanceM: number }[], pointerX: number, scale: Pick<ProfileScale, 'left' | 'width' | 'totalDistanceM'>): ProfileHoverTarget {
  const targetDistanceM = profileDistanceAtX(pointerX, scale);
  const index = nearestProfileSampleIndex(samples, targetDistanceM);
  return { index, x: profileXAtDistance(samples[index]?.distanceM ?? 0, scale) };
}

export function profileTooltipPlacement(pointerX: number, pointerY: number, stageWidth: number, stageHeight: number, tooltipWidth = PROFILE_TOOLTIP_WIDTH_PX, tooltipHalfHeight = PROFILE_TOOLTIP_HALF_HEIGHT_PX): ProfileTooltipPlacement {
  const margin = 8;
  const gap = 12;
  const rightSpace = stageWidth - pointerX - gap;
  const leftSpace = pointerX - gap;
  const onLeft = rightSpace < tooltipWidth && leftSpace > rightSpace;
  const minimumTop = margin + tooltipHalfHeight;
  const maximumTop = stageHeight - margin - tooltipHalfHeight;
  return {
    onLeft,
    top: maximumTop < minimumTop ? stageHeight / 2 : clamp(pointerY, minimumTop, maximumTop),
  };
}
